import '@/handler/axiosAuthInterceptor'
import Storage from '@/handler/storageLocal'
import Session from '@/handler/session'
import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import ConfigAPI from '@/config/api'
import ModuleViewHeading from '@/components/inc/module/ModuleViewHeading'

const APP_BUILD_VERSION_KEY = 'AppBuildVersion'
const APP_BUILD_RELOAD_KEY = 'AppBuildVersionReload'

async function clearServiceWorkerAndCaches() {
  try {
    if ('serviceWorker' in navigator) {
      // Desregistrar el SW invalida la PushSubscription del navegador (su
      // vida está atada a esta registration). Sin este reset, el flag de
      // "ya suscripto" quedaba en sessionStorage (sobrevive al reload) y la
      // re-suscripción automática de más abajo se salteaba creyendo que no
      // hacía falta — el usuario quedaba con las notificaciones push
      // apagadas después de cada actualización, sin darse cuenta.
      try {
        require('./handler/notifications.js').invalidatePushSubscriptionState()
      } catch (error) {}

      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))
    }
  } catch (error) {}

  try {
    if ('caches' in window) {
      const cacheKeys = await caches.keys()
      await Promise.all(cacheKeys.map((key) => caches.delete(key)))
    }
  } catch (error) {}
}

let deferredUpdateRetryPending = false

function checkAppBuildVersion() {
  fetch('/version.json?_=' + Date.now(), { cache: 'no-store' })
    .then((response) => (response.ok ? response.json() : null))
    .then(async (data) => {
      if (!data || !data.buildId) return

      const remoteBuildId = data.buildId.toString()
      const currentBuildId = Storage.getValue(APP_BUILD_VERSION_KEY)

      if (currentBuildId && currentBuildId !== remoteBuildId) {
        const reloadedBuildId = sessionStorage.getItem(APP_BUILD_RELOAD_KEY)
        if (reloadedBuildId !== remoteBuildId) {
          // Si una pantalla crítica está en medio de algo (pedido a medio
          // cargar, guardado o sincronización en curso), la recarga se
          // difiere: se avisa a la pantalla y se reintenta en 1 minuto.
          let busy = false
          try { busy = typeof window.__appBusy === 'function' && !!window.__appBusy() } catch (e) { busy = false }
          if (busy) {
            window.dispatchEvent(new CustomEvent('app-update-deferred'))
            if (!deferredUpdateRetryPending) {
              deferredUpdateRetryPending = true
              setTimeout(() => {
                deferredUpdateRetryPending = false
                checkAppBuildVersion()
              }, 60 * 1000)
            }
            return
          }

          sessionStorage.setItem(APP_BUILD_RELOAD_KEY, remoteBuildId)
          await clearServiceWorkerAndCaches()

          // El guard global de beforeunload (ModalProtectionMixin en app.vue)
          // bloquearía esta recarga con el diálogo "¿Salir del sitio?". Acá
          // ya se validó que la app no está ocupada, así que se suprime.
          window.__suppressBeforeUnload = true

          const currentUrl = new URL(window.location.href)
          currentUrl.searchParams.set('_v', remoteBuildId)
          window.location.replace(currentUrl.toString())
          return
        }
      }

      Storage.setValue(APP_BUILD_VERSION_KEY, remoteBuildId)
      if (sessionStorage.getItem(APP_BUILD_RELOAD_KEY) === remoteBuildId) {
        sessionStorage.removeItem(APP_BUILD_RELOAD_KEY)
      }
    })
    .catch(() => {})
}

window.__checkAppVersion = checkAppBuildVersion
checkAppBuildVersion()

// Re-chequear la versión durante la sesión: los vendedores dejan la PWA
// abierta días y el chequeo del arranque no alcanza para enterarse de un
// deploy. Si no hay conexión el fetch falla silenciosamente y no pasa nada.
const APP_VERSION_CHECK_INTERVAL = 10 * 60 * 1000
setInterval(checkAppBuildVersion, APP_VERSION_CHECK_INTERVAL)
window.addEventListener('online', checkAppBuildVersion)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') checkAppBuildVersion()
})

// Una sesión puede quedar abierta mientras vence el JWT sin que el usuario
// haga ninguna request. Se verifica localmente (claim `exp`) para devolverlo
// al login en ese momento; no se hace polling al API. Los tokens revocados o
// inválidos siguen pasando por el mismo flujo de token_error de siempre.
const JWT_EXPIRATION_CHECK_INTERVAL = 60 * 1000
function checkJwtExpiration() {
  Session.checkAuthTokenExpiration()
}
checkJwtExpiration()
setInterval(checkJwtExpiration, JWT_EXPIRATION_CHECK_INTERVAL)
document.addEventListener('visibilitychange', () => {
  if(document.visibilityState === 'visible') checkJwtExpiration()
})

// Pedir almacenamiento persistente para que el navegador no evicte la sesión,
// el catálogo offline (IndexedDB) ni el cache de impresión. Crítico para abrir
// y facturar offline tras varios días sin conexión.
async function requestPersistentStorage() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      const already = await navigator.storage.persisted()
      if (!already) await navigator.storage.persist()
    }
  } catch (error) { /* API no disponible o denegada: se sigue sin persistencia garantizada */ }
}
requestPersistentStorage()

// El service worker cachea JS/CSS "cache primero" sin invalidación por
// contenido (public/service-worker.js usa un CACHE_NAME fijo en dev; en
// build, generate-sw.js le pone el buildId real). Registrarlo en
// `vue-cli-service serve` hace que cualquier cambio guardado quede invisible
// hasta desregistrar el SW y limpiar caches a mano — por eso solo se
// registra en producción.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.register('/service-worker.js')
    .then((registration) => {
      console.log("Service Worker Registered!")

      // Forzar la búsqueda del service worker nuevo: una SPA con hash routing
      // no genera navegaciones, así que el navegador puede tardar hasta 24h en
      // revisar /service-worker.js por su cuenta. El SW nuevo hace
      // skipWaiting + claim, así que apenas se descarga toma control.
      const swUpdate = () => { registration.update().catch(() => {}) }
      setInterval(swUpdate, APP_VERSION_CHECK_INTERVAL)
      window.addEventListener('online', swUpdate)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') swUpdate()
      })

      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data && event.data.type === 'SW_ACTIVATED') {
          window.dispatchEvent(new CustomEvent('sw-activated'))
        }
      })

      try {
        var cachedConfig = null
        try { cachedConfig = JSON.parse(Storage.getValue('ConfigurationNotificationsPush')) } catch(e) { cachedConfig = null }

        if (cachedConfig && cachedConfig.vapidPublicKey) {
          require("./handler/notifications.js").autoSubscribeIfNeeded()
        } else {
          Vue.use(VueAxios, axios)
          const instance = axios.create()
          instance.get(ConfigAPI.baseURL + "public/notifications/push/1/configuration")
            .then((res) => {
              Storage.setValue('ConfigurationNotificationsPush', JSON.stringify(res.data))
              try { require("./handler/notifications.js").autoSubscribeIfNeeded() } catch(e) { /* push no disponible */ }
            })
            .catch(() => {})
        }
      } catch(e) {
        // Push no disponible (sin conexión o el navegador no lo soporta)
      }
    })
    .catch(err => console.warn("Service Worker no disponible:", err))
}

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import 'core-js/es6/promise'
import 'core-js/es6/string'
import 'core-js/es7/array'
// import cssVars from 'css-vars-ponyfill'
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue'
import App from './app'
import router from './router'
import VueAWN from "vue-awesome-notifications"
import Loading from 'vue-loading-overlay';
import moment from 'moment'
import excel from 'vue-excel-export'
import VueLazyload from 'vue-lazyload'
import Theme from '@/config/theme'
import vSelect from 'vue-select'
import AppTextarea from '@/components/inc/textarea/AppTextarea'
import AppNumberInput from '@/components/inc/input/AppNumberInput'
import AppTextInput from '@/components/inc/input/AppTextInput'
import 'material-symbols/rounded.css'
import AppIcon from '@/components/inc/icon/AppIcon'
import EmptyState from '@/components/inc/state/EmptyState'
import DepositLocationLabel from '@/components/inc/deposits/DepositLocationLabel'
import ZyncroLoader from '@/components/branding/ZyncroLoader'

// Select2
Vue.component('v-select', vSelect)
Vue.component('ModuleViewHeading', ModuleViewHeading)
Vue.component('AppIcon', AppIcon)
Vue.component('EmptyState', EmptyState)
Vue.component('DepositLocationLabel', DepositLocationLabel)
Vue.component('ZyncroLoader', ZyncroLoader)
// End select2

// Reemplazo de b-form-textarea (ver comentario en el propio componente)
Vue.component('app-textarea', AppTextarea)
Vue.component('app-number-input', AppNumberInput)
Vue.component('app-text-input', AppTextInput)
 
// todo
// cssVars()

// BModal.ignoreEnforceFocusSelector: sin esto, cualquier b-modal abierto le
// roba el foco de vuelta a sí mismo apenas el input del Command Palette
// (Ctrl+K) lo recibe -- BootstrapVue fuerza el foco a permanecer dentro del
// modal más reciente en cada evento `focusin` del documento. El Command
// Palette es un div propio (no un b-modal), así que sin este selector queda
// afuera de esa lógica y nunca puede quedarse con el foco mientras haya un
// modal abierto debajo.
Vue.use(BootstrapVue, {
  BModal: {
    ignoreEnforceFocusSelector: '.cp-backdrop, .cp-backdrop *',
  },
});
Vue.use(BootstrapVueIcons)
Vue.use(VueAWN,{
  maxNotifications: 1,
  labels: {
    tip: 'Ayuda',
    info: 'Información',
    success: 'Listo',
    warning: 'Atención',
    alert: 'Error',
    async: 'Procesando',
    confirm: 'Confirmación'
  },
  icons: {
    enabled: true,
    prefix: "<span class='material-symbols-rounded awn-material-icon'>",
    suffix: '</span>',
    tip: 'help',
    info: 'info',
    success: 'check_circle',
    warning: 'warning',
    alert: 'error',
    async: 'progress_activity',
    confirm: 'warning'
  }
});

Vue.use(Loading,{
    //container: this.$refs.loadingContainer,
    color: Theme.PRIMARY_COLOR,
    loader: 'ZyncroLoader',
    width: 128,
    height: 128,
    backgroundColor: '#ffffff',
    opacity: 0.72,
    blur: '1px',
})
Vue.use(excel)
Vue.use(VueLazyload)

moment.locale('es')
Vue.prototype.moment = moment

/* eslint-disable no-new */
var VueApp =  new Vue({
  el: '#app',
  router,
  template: '<App/>',
  components: {
    App
  }
})
